import { getWorkoutName } from '@strength/domain';
import { M } from '@strength/loco-sync';
import { inArray } from '@teamworksdev/loco-sync-client';
import { useMemo } from 'react';
import { useQuery } from '../../../../../loco-sync';
import { deriveWorkoutEventStatus } from '../components/mainCalendar/workout-event-drag';

export type CalendarWorkoutEvent = {
  id: string;
  workoutId: string;
  blockId: string;
  workoutName: string;
  trackId: string;
  assignedDate: string;
  isCurrent: boolean;
  /** The parent block's date range — a drag may not leave it (SC-5441). */
  blockStartDate: string;
  blockEndDate: string;
  /**
   * The workout's status for drag purposes: the published layer's status,
   * with in-progress derived from logged actuals (a started workout's
   * layer status stays 'planned'; see workout-event-drag). Only planned
   * workouts are draggable.
   */
  status: string;
};

export type CalendarSyncData = {
  eventsByTrack: Map<string, CalendarWorkoutEvent[]>;
};

export function useCalendarSync(
  athleteId: string | null,
  trackIds: string[],
  showCanceled: boolean,
): CalendarSyncData {
  const { data: blocks = [] } = useQuery('Block', {
    trackId: trackIds.length > 0 ? inArray(trackIds) : undefined,
  });

  const blockIds = useMemo(() => blocks.map((b) => b.id), [blocks]);

  const { data: blockWorkoutLayers = [] } = useQuery(
    'BlockPublishedWorkoutLayer',
    {
      blockId: blockIds.length > 0 ? inArray(blockIds) : undefined,
      isCurrent: true,
      ...(showCanceled
        ? {}
        : { status: inArray(['planned', 'completed', 'skipped']) }),
    },
  );

  const { data: athleteWorkoutLayers = [] } = useQuery(
    'AthletePublishedWorkoutLayer',
    {
      blockId: blockIds.length > 0 ? inArray(blockIds) : undefined,
      profileId: athleteId ?? undefined,
      isCurrent: true,
      ...(showCanceled
        ? {}
        : { status: inArray(['planned', 'completed', 'skipped']) }),
    },
  );

  // Actuals drive the in-progress derivation (a started workout's layer
  // status stays 'planned'). In the athlete view only the selected
  // athlete's actuals matter — the builder's per-athlete status does the
  // same; in the team view any athlete's actuals mark the workout started.
  const { data: actualWorkoutLayers = [] } = useQuery(
    'AthleteActualWorkoutLayer',
    {
      blockId: blockIds.length > 0 ? inArray(blockIds) : undefined,
      ...(athleteId ? { profileId: athleteId } : {}),
    },
  );

  const workoutsWithActuals = useMemo(
    () => new Set(actualWorkoutLayers.map((layer) => layer.workoutId)),
    [actualWorkoutLayers],
  );

  const blockMap = useMemo(() => {
    const map = new Map<string, M['Block']>();
    for (const block of blocks) {
      map.set(block.id, block);
    }
    return map;
  }, [blocks]);

  // Group workout events by trackId
  const eventsByTrack = useMemo(() => {
    const layers =
      athleteId === null ? blockWorkoutLayers : athleteWorkoutLayers;
    const grouped = new Map<string, CalendarWorkoutEvent[]>();

    for (const layer of layers) {
      const block = blockMap.get(layer.blockId);
      if (!block) continue;
      // Flex workouts (null assignedDate) aren't placed on the calendar grid.
      if (!layer.assignedDate) continue;

      const event: CalendarWorkoutEvent = {
        id: layer.id,
        workoutId: layer.workoutId,
        blockId: layer.blockId,
        workoutName: getWorkoutName(layer, block, false),
        trackId: block.trackId,
        assignedDate: layer.assignedDate,
        isCurrent: layer.isCurrent,
        blockStartDate: block.startDate,
        blockEndDate: block.endDate,
        status: deriveWorkoutEventStatus({
          layerStatus: layer.status,
          hasActual: workoutsWithActuals.has(layer.workoutId),
        }),
      };

      const trackEvents = grouped.get(block.trackId);
      if (trackEvents) {
        trackEvents.push(event);
      } else {
        grouped.set(block.trackId, [event]);
      }
    }

    return grouped;
  }, [
    athleteId,
    blockWorkoutLayers,
    athleteWorkoutLayers,
    blockMap,
    workoutsWithActuals,
  ]);

  return {
    eventsByTrack,
  };
}
